/**
 * Reading the truncation signal off a capped export (CR-7432).
 *
 * Exports download as blobs, so the response body IS the file — there is
 * nowhere in it to put a message the UI can read. The backend therefore
 * reports truncation in two response headers, and this module is the single
 * place that knows their names and how to word what they mean.
 *
 * Absence of the header means a complete export. That is deliberate: a
 * complete file is unchanged in every respect, including its headers.
 *
 * @link backend/core/export_truncation.py for the writer side
 */
import { toast } from 'sonner';

/** Header carrying the TRUE total. Axios lowercases all response header keys. */
const TRUNCATION_HEADER = 'x-export-truncated';

/** Header carrying how many rows actually reached the file. */
const EXPORTED_ROWS_HEADER = 'x-export-rows';

export interface ExportTruncation {
  /** Rows that matched the filter. */
  total: number;
  /**
   * Rows that actually reached the file. 0 when the server sent only the
   * total — older endpoints (CR-8193's Stock Planning export) send one header.
   */
  written: number;
}

/** Shape we need off an axios response, without depending on axios here. */
interface HeaderCarrier {
  headers?: Record<string, unknown>;
}

/**
 * Pull the truncation signal off an export response.
 *
 * @param response - the axios response for a blob export
 * @returns the two counts, or null when the export was complete
 */
export function readExportTruncation(response: HeaderCarrier): ExportTruncation | null {
  const total = Number(response.headers?.[TRUNCATION_HEADER]);
  if (!Number.isFinite(total) || total <= 0) return null;
  const written = Number(response.headers?.[EXPORTED_ROWS_HEADER]);
  return {
    total,
    written: Number.isFinite(written) && written > 0 ? written : 0,
  };
}

/**
 * The one sentence, worded exactly as the notice row inside the file.
 *
 * @param truncation - counts from {@link readExportTruncation}
 * @returns the message shown in the toast and the banner
 */
export function exportTruncationMessage(truncation: ExportTruncation): string {
  const total = truncation.total.toLocaleString();
  if (!truncation.written) {
    // Only the total reached us. Still worth saying — the user otherwise has
    // no way to know the file is short.
    return `Export cut short: ${total} rows matched — narrow the filter to export the rest.`;
  }
  return `Showing the first ${truncation.written.toLocaleString()} of ${total} rows — `
    + 'narrow the filter to export the rest.';
}

/**
 * Report the outcome of an export: a warning naming both numbers when the file
 * was cut short, the normal success message when it was not.
 *
 * The toast is half the answer — it auto-dismisses, and by the time the user
 * opens the file it is gone. Callers should also render the returned value
 * through `ExportTruncationBanner`, which stays put.
 *
 * @param response - the axios response for a blob export
 * @param successMessage - what to say when the export was complete
 * @returns the truncation counts when cut short, else null
 */
export function notifyExportResult(
  response: HeaderCarrier,
  successMessage: string,
): ExportTruncation | null {
  const truncation = readExportTruncation(response);
  if (truncation) {
    toast.warning(exportTruncationMessage(truncation));
  } else {
    toast.success(successMessage);
  }
  return truncation;
}
